import { ApiContainerResponse } from '@donation/containers/models/containers.response';
import { DonationItemResponse, DonationResponse } from '@donation/donations/models/donations.response';
import { ApiDonorResponse as DonorResponse } from '@donation/donors/models/donor-management.response';

import { DonationScope } from '@core/models/domain.enum';
import {
  Container,
  DeptCategory,
  DonationDepartment,
  Donor,
  ScheduledDonation,
  SubCategory
} from '@core/models/domain.models';

import { SharedCategoryItem, SharedDepartmentItem } from './new-donation.response';
import { ReviewItemState, SelectedDonor, SelectedItemsMap, StepDefinition } from './new-donation.state';

// ── Shared Department / Category mappers ──────────────────────────────────

export function mapSharedDepartmentsToDonationDepartments(items: SharedDepartmentItem[]): DonationDepartment[] {
  return items.map((item, index) => ({
    key: item.id.toString(),
    name: item.name,
    icon: '',
    color: '',
    estimatedValue: 0,
    active: true,
    sortOrder: index,
    categories: []
  }));
}

export function mapSharedCategoriesToDeptCategories(items: SharedCategoryItem[]): DeptCategory[] {
  return items.map(item => ({
    key: item.id.toString(),
    name: item.name,
    estimatedValue: 0,
    subCategories: (item.subCategories ?? []).map(
      (sub): SubCategory => ({
        key: sub.id.toString(),
        name: sub.name,
        icon: '',
        estimatedValue: 0
      })
    )
  }));
}

export function mapDonorToSelected(d: Donor): SelectedDonor {
  const hasName = !!(d.firstName?.trim() || d.lastName?.trim());
  const displayName = hasName
    ? `${d.firstName ?? ''} ${d.lastName ?? ''}`.trim()
    : d.referenceNumber?.trim() || 'Anonymous';
  const initials = hasName
    ? `${d.firstName?.[0] ?? ''}${d.lastName?.[0] ?? ''}`.toUpperCase()
    : d.referenceNumber
      ? d.referenceNumber.slice(0, 2).toUpperCase()
      : 'AN';
  return {
    id: d.id,
    displayName,
    initials,
    tier: d.loyaltyTier,
    points: d.loyaltyPoints,
    totalVisits: d.totalDonations,
    phone: d.phone,
    email: d.email || undefined
  };
}

export function computeTotalItems(items: SelectedItemsMap): number {
  return Object.values(items).reduce((s, i) => s + i.qty, 0);
}

export function computeTotalValue(items: SelectedItemsMap, departments: DonationDepartment[]): number {
  let total = 0;
  for (const [key, entry] of Object.entries(items)) {
    const parts = key.split('::');
    const dept = departments.find(d => d.key === parts[0]);
    const cat = parts[1] ? dept?.categories.find(c => c.key === parts[1]) : null;
    const sub = parts[2] && cat ? cat.subCategories?.find(s => s.key === parts[2]) : null;
    const base = sub?.estimatedValue ?? cat?.estimatedValue ?? dept?.estimatedValue ?? 0;
    total += (entry.estimatedValue ?? base) * entry.qty;
  }
  return total;
}

export function computeEstimatedPoints(totalItems: number, pointsPerItem: number): number {
  return totalItems * pointsPerItem;
}

export function buildItemKey(deptKey: string, catKey?: string | null, subKey?: string | null): string {
  if (!catKey) return deptKey;
  if (!subKey) return `${deptKey}::${catKey}`;
  return `${deptKey}::${catKey}::${subKey}`;
}

export function buildSteps(donationType: DonationScope | null): StepDefinition[] {
  const base: StepDefinition[] = [{ n: 1, label: 'Identify Donor' }];
  if (donationType === DonationScope.Monetary) {
    return [...base, { n: 2, label: 'Add Money' }, { n: 3, label: 'Review' }];
  }
  if (donationType === DonationScope.Both) {
    return [...base, { n: 2, label: 'Add Items' }, { n: 3, label: 'Add Money' }, { n: 4, label: 'Review' }];
  }
  if (donationType === DonationScope.Box) {
    return [...base, { n: 2, label: 'Number of Boxes' }, { n: 3, label: 'Review' }];
  }
  if (donationType === DonationScope.Bag) {
    return [...base, { n: 2, label: 'Number of Bags' }, { n: 3, label: 'Review' }];
  }
  if (donationType === DonationScope.ByWeight) {
    return [...base, { n: 2, label: 'Weight' }, { n: 3, label: 'Review' }];
  }
  return [...base, { n: 2, label: 'Add Items' }, { n: 3, label: 'Review' }];
}

export function buildReviewItems(
  selectedItems: SelectedItemsMap,
  departments: DonationDepartment[]
): ReviewItemState[] {
  return Object.entries(selectedItems).map(([key, entry]) => {
    const parts = key.split('::');
    const dept = departments.find(d => d.key === parts[0]);
    const cat = parts[1] ? dept?.categories.find(c => c.key === parts[1]) : null;
    const sub = parts[2] && cat ? cat.subCategories?.find(s => s.key === parts[2]) : null;
    const base = sub?.estimatedValue ?? cat?.estimatedValue ?? dept?.estimatedValue ?? 0;
    const name = sub ? `${cat?.name ?? ''} › ${sub.name}` : cat ? cat.name : (dept?.name ?? key);
    return { key, name, qty: entry.qty, total: (entry.estimatedValue ?? base) * entry.qty };
  });
}

export function computeDeptCount(selectedItems: SelectedItemsMap, deptKey: string): number {
  return Object.entries(selectedItems)
    .filter(([k]) => k === deptKey || k.startsWith(deptKey + '::'))
    .reduce((s, [, v]) => s + v.qty, 0);
}

export function isAddItemsStep(step: number, donationType: DonationScope | null): boolean {
  return (
    step === 2 &&
    donationType !== DonationScope.Monetary &&
    donationType !== DonationScope.Box &&
    donationType !== DonationScope.Bag &&
    donationType !== DonationScope.ByWeight
  );
}

export function isAddBoxBagStep(step: number, donationType: DonationScope | null): boolean {
  return step === 2 && (donationType === DonationScope.Box || donationType === DonationScope.Bag);
}

export function isAddPoundageStep(step: number, donationType: DonationScope | null): boolean {
  return step === 2 && donationType === DonationScope.ByWeight;
}

export function isAddMoneyStep(step: number, donationType: DonationScope | null): boolean {
  return (step === 2 && donationType === DonationScope.Monetary) || (step === 3 && donationType === DonationScope.Both);
}

export function isReviewStep(step: number, donationType: DonationScope | null): boolean {
  return (step === 3 && donationType !== DonationScope.Both) || step === 4;
}

export function getTopTitle(step: number, donationType: DonationScope | null): string {
  const totalSteps = buildSteps(donationType).length;
  const label = getStepLabel(step, donationType);
  return `Step ${step} of ${totalSteps} — ${label}`;
}

function getStepLabel(step: number, donationType: DonationScope | null): string {
  if (step === 1) return 'Identify Donor';
  if (step === 2) {
    if (donationType === DonationScope.Monetary) return 'Add Money';
    if (donationType === DonationScope.Box) return 'Number of Boxes';
    if (donationType === DonationScope.Bag) return 'Number of Bags';
    if (donationType === DonationScope.ByWeight) return 'Weight';
    return 'Add Items';
  }
  if (step === 3) return donationType === DonationScope.Both ? 'Add Money' : 'Review & Complete';
  return 'Review & Complete';
}

export function getTopSubtitle(step: number, donationType: DonationScope | null): string {
  if (step === 1) return 'Scan QR, search by phone, or continue anonymously';
  if (isAddMoneyStep(step, donationType)) return 'Select amount and payment method';
  if (isAddBoxBagStep(step, donationType)) return 'Enter the number of items to donate';
  if (isAddPoundageStep(step, donationType)) return 'Enter the total weight in pounds';
  if (isAddItemsStep(step, donationType)) return 'Quick-tap categories — complete in under 10 seconds';
  return 'Confirm details and generate receipt';
}

// ── API path mappers ──────────────────────────────────────────────────────────

export function mapApiDonorToSelected(d: DonorResponse): SelectedDonor {
  // Prefer the unified `name` field the API sends; fall back to firstName + lastName.
  const apiName = d.name?.trim() ?? '';
  const first = apiName ? apiName.split(' ')[0] : (d.firstName?.trim() ?? '');
  const last = apiName ? apiName.slice(first.length).trim() : (d.lastName?.trim() ?? '');
  const hasName = !!(apiName || first || last);
  const displayName = hasName ? apiName || `${first} ${last}`.trim() : d.referenceNumber?.trim() || 'Anonymous';
  const initials = hasName
    ? `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase()
    : d.referenceNumber
      ? d.referenceNumber.slice(0, 2).toUpperCase()
      : 'AN';
  return {
    id: d.id,
    displayName,
    initials,
    tier: d.loyaltyTier,
    points: d.loyaltyPoints,
    totalVisits: d.totalVisits ?? d.totalDonations ?? 0,
    phone: d.phoneNumber ?? d.phone ?? '',
    email: d.email ?? ''
  };
}

export function mapApiDonorToDonor(d: DonorResponse): Donor {
  // Prefer the unified `name` field; split into first/last for the domain model.
  const apiName = d.name?.trim() ?? '';
  const first = apiName ? apiName.split(' ')[0] : (d.firstName ?? d.referenceNumber);
  const last = apiName ? apiName.slice((first ?? '').length).trim() : (d.lastName ?? '');
  return {
    id: d.id,
    referenceNumber: d.referenceNumber,
    firstName: first ?? '',
    lastName: last,
    email: d.email ?? '',
    phone: d.phoneNumber ?? d.phone ?? '',
    loyaltyTier: d.loyaltyTier,
    loyaltyPoints: d.loyaltyPoints,
    totalDonations: d.totalVisits ?? d.totalDonations,
    lifetimeValue: d.lifetimeValue ?? 0,
    joinDate: new Date(d.joinDate),
    lastDonationDate: d.lastDonationDate ? new Date(d.lastDonationDate) : undefined,
    preferredStoreId: d.preferredStoreId ?? undefined
  };
}

export function mapApiContainerToContainer(c: ApiContainerResponse): Container {
  return {
    id: c.id,
    referenceNumber: c.referenceNumber,
    barcode: c.barcode,
    donationId: c.donationId ?? undefined,
    containerType: c.containerType,
    presortMethod: c.presortMethod ?? undefined,
    contents: [],
    destination: c.destination ?? undefined,
    status: c.status,
    deptKey: c.departmentKey ?? undefined,
    catKey: c.categoryKey ?? undefined,
    storeId: c.storeId,
    storeName: `Store ${c.storeId}`,
    createdAt: new Date(),
    updatedAt: new Date(),
    totalItems: c.totalItems,
    totalEstimatedValue: c.totalEstimatedValue,
    salvageWeightLbs: c.salvageWeightLbs ?? undefined,
    isSeasonal: c.isSeasonal,
    seasonalTag: c.seasonalTag ?? undefined
  };
}

export function mapApiDonationToScheduledDonation(d: DonationResponse): ScheduledDonation {
  return {
    id: d.id,
    referenceNumber: d.referenceNumber,
    donorId: d.donorId ?? undefined,
    donorName:
      d.donor?.name?.trim() ||
      d.guestName ||
      (d.donorId !== null && d.donorId !== undefined ? d.referenceNumber : 'Walk-in'),
    donorPhone: (d.donor?.phone || d.guestPhone) ?? undefined,
    donorEmail: (d.donor?.email || d.guestEmail) ?? undefined,
    address: d.pickupAddress ?? undefined,
    storeId: d.storeId,
    storeName: d.store?.name ?? undefined,
    method: d.method,
    scope: d.scope,
    date: new Date(d.scheduledDate ?? d.donationAt),
    timeSlot: extractTimeSlot(d.donationAt),
    status: d.status,
    itemCount: d.totalItems,
    monetaryAmount:
      (d.items ?? []).filter(i => i.scope === DonationScope.Monetary).reduce((sum, i) => sum + (i.amount ?? 0), 0) ||
      undefined,
    categories: extractScheduledCategories(d.items),
    recurring: d.recurring ?? undefined,
    notes: d.notes ?? undefined,
    createdAt: new Date(d.donationAt)
  };
}

function extractScheduledCategories(items: DonationItemResponse[] | undefined): string[] {
  const seen = new Set<string>();
  for (const item of items ?? []) {
    if (item.scope === DonationScope.Monetary) continue;
    const label = item.subCategoryKey ?? item.categoryKey ?? item.departmentKey;
    if (label) seen.add(label);
  }
  return Array.from(seen);
}

function extractTimeSlot(scheduledDate?: string | null): string {
  if (!scheduledDate) return '—';
  const dt = new Date(scheduledDate);
  if (isNaN(dt.getTime())) return '—';
  const h = dt.getHours();
  const m = dt.getMinutes();
  if (h === 0 && m === 0) return '—';
  const meridiem = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  const min = m.toString().padStart(2, '0');
  return `${hour}:${min} ${meridiem}`;
}

export function mapDonationItemsToSelectedMap(
  items: DonationItemResponse[],
  departments?: DonationDepartment[]
): SelectedItemsMap {
  const map: SelectedItemsMap = {};
  for (const item of items) {
    if (item.scope === DonationScope.Monetary) continue;
    const qty = item.quantity ?? 1;
    let key: string | null = null;

    // Path 1: numeric IDs from API (new API fields — most reliable)
    if (item.departmentId !== null && item.departmentId !== undefined) {
      const deptKey = item.departmentId.toString();
      const catKey = item.categoryId !== null && item.categoryId !== undefined ? item.categoryId.toString() : null;
      key = buildItemKey(deptKey, catKey, item.subCategoryKey ?? null);
    }
    // Path 2: departmentKey directly matches a loaded dept key
    else if (item.departmentKey && departments) {
      const dept = departments.find(d => d.key === item.departmentKey);
      if (dept) {
        let catKey: string | null = null;
        if (item.categoryKey) {
          const cat =
            dept.categories.find(c => c.key === item.categoryKey) ??
            dept.categories.find(c => c.name === item.categoryKey);
          catKey = cat?.key ?? item.categoryKey;
        }
        key = buildItemKey(dept.key, catKey, item.subCategoryKey ?? null);
      }
    }
    // Path 3: scan departments for matching category key/name (mock data — items have no departmentKey)
    if (!key && departments && item.categoryKey) {
      for (const dept of departments) {
        const cat =
          dept.categories.find(c => c.key === item.categoryKey) ??
          dept.categories.find(c => c.name === item.categoryKey);
        if (cat) {
          key = buildItemKey(dept.key, cat.key, item.subCategoryKey ?? null);
          break;
        }
      }
    }
    // Path 4: departmentKey name-based scan (old API data without numeric IDs)
    if (!key && item.departmentKey && departments) {
      const dept = departments.find(d => d.name === item.departmentKey);
      if (dept) {
        const cat = item.categoryKey
          ? (dept.categories.find(c => c.key === item.categoryKey) ??
            dept.categories.find(c => c.name === item.categoryKey))
          : null;
        key = buildItemKey(dept.key, cat?.key ?? null, item.subCategoryKey ?? null);
      }
    }
    // Path 5: raw fallback — use keys verbatim (backward compat, no departments available)
    if (!key && item.departmentKey) {
      key = buildItemKey(item.departmentKey, item.categoryKey ?? null, item.subCategoryKey ?? null);
    }

    if (key) {
      map[key] = { qty: (map[key]?.qty ?? 0) + qty };
    }
  }
  return map;
}
